import { FC, useEffect, useMemo, useRef } from "react";
import { Text, Textarea, Switch } from "rizzui";
import CustomSelect from "../../../components/forms/CustomSelect";
import FormLabel from "../../../components/forms/FormLabel";
import { useFormik } from "formik";
import { aeroStatus, age, altitudeLevel, distanceToAero, flightType, sex, statusAeroOptions, windDirectionsSpanish, yesNoSelector } from "../../../utils/SelectorMapping";
import WeatherSelectors from "../../../components/commonForm/WeatherSelectors";
import RequiredInput from "../../../components/forms/InputHOC";
import { DatePicker } from "../../../components/forms/DatePicker";
import moment from "moment";
import { useCensusFormContext } from "./provider/CensusFormProvider";
import { createLineValidationSchema } from "./types/CensusSchemas";
import CensusLinePhotosGallery from "./CensusLinePhotosGallery";
import { normalizeWindFieldsForForm } from "../../../utils/censusLineUtils";

interface CensusLineFormProps {
    initialValues?: any;
    initialTouched?: any;
    lineNumber: number;
    onValidationChange?: (isValid: boolean) => void;
    onFormChange?: (values: any) => void;
    lineId?: string;
    parkId?: string;
    censusStartTime?: string;
    censusEndTime?: string;
}

const CensusLineForm: FC<CensusLineFormProps> = ({
    initialValues,
    initialTouched,
    lineNumber,
    onValidationChange,
    onFormChange,
    lineId,
    parkId,
    censusStartTime,
    censusEndTime,
}) => {
    
    // PROVIDER
    
    const { turbinesList, speciesList } = useCensusFormContext();

    const lineValidationSchema = useMemo(
        () => createLineValidationSchema(censusStartTime, censusEndTime),
        [censusStartTime, censusEndTime]
    );

    const lineKey = lineId ?? `line-${lineNumber}`;
    const prevLineKeyRef = useRef<string | null>(null);
    const isInitialMountRef = useRef(true);
    
    // FORMIK

    const formik = useFormik({
        initialValues: initialValues || {
            specieId: null,
            turbineId:null,
            windDirection: null,
            windSpeed: null,
            cloudCoverage: null,
            visibility: null,
            numIndividuals: null,
            aeroStatus: 'encendido',
            altitudLevel: null,
            distanceToAero: null,
            riskDetected: false,
            isOfc: 'false',
            dirOrigen: null,
            dirDest: null,
            reacc: null,
            aeroOnComments: null,
            askStop: null,
            rain: null,
            askAnillado: null,
            sex: '0',
            age: '0',
            flightDirection: null,
            birdReaction: null,
            flightType: null,
            anillado: null,
            photos: [],
            isProtected: false,
            censusMin: null,
            time: null,
            extraAnillado: null,
        },
        initialTouched: initialTouched || {},
        enableReinitialize: false,
        validationSchema: lineValidationSchema,
        onSubmit: () => {},
        validateOnChange: true,
        validateOnBlur: true,
    });

    // USE EFFECT

    useEffect(() => {
        if (!initialValues) {
            return;
        }
        if (isInitialMountRef.current || prevLineKeyRef.current !== lineKey) {
            const wind = normalizeWindFieldsForForm(
                initialValues.windDirection,
                initialValues.windSpeed
            );
            formik.resetForm({
                values: { ...initialValues, ...wind },
                touched: initialTouched || {},
            });
            isInitialMountRef.current = false;
            prevLineKeyRef.current = lineKey;
        }
    }, [lineKey, initialValues, initialTouched]);

    useEffect(() => {
        if (formik.values.time) {
            formik.validateField('time');
        }
    }, [censusStartTime, censusEndTime, lineValidationSchema]);

    useEffect(() => {
        if (onValidationChange) {
            const isValid = Object.keys(formik.errors).length === 0;
            onValidationChange(isValid);
        }
    }, [formik.errors, onValidationChange]);

    useEffect(() => {
        onFormChange?.(formik.values);
    }, [formik.values]);

    useEffect(() => {
        if (formik.values.aeroStatus === 'apagado' && formik.values.askStop !== 'no') {
            formik.setFieldValue('askStop', 'no');
        }
    }, [formik.values.aeroStatus]);

    // RENDER

    return (
        <div className="space-y-6">
            <Text className="text-lg font-medium">Línea de Censo {lineNumber}</Text>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* TIME */}

                <div className="flex flex-col">
                    <FormLabel label="Hora" required>
                        <DatePicker
                            className={
                                formik.errors.time && (formik.touched.time || formik.values.time)
                                    ? 'invalid-form-input w-full'
                                    : 'w-full'
                            } 
                            placeholderText="Selecciona la hora"
                            selected={
                                formik.values.time
                                ? moment(formik.values.time, 'HH:mm', true).isValid()
                                    ? moment(formik.values.time, 'HH:mm').toDate()
                                    : null
                                : null
                            }
                            onChange={async (date: any) => {
                                const formattedDate = moment(date).format('HH:mm');
                                await formik.setFieldValue('time', formattedDate, true);
                                formik.setFieldTouched('time', true, false);
                            }}
                            onBlur={() => {
                                formik.setFieldTouched('time', true);
                                formik.validateField('time');
                            }}
                            dateFormat="HH:mm"
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={5}
                            timeCaption="Hora"
                            timeFormat="HH:mm"
                            required
                        />
                    </FormLabel>
                    {formik.errors.time && (formik.touched.time || formik.values.time) && (
                        <Text className="text-red-500 text-sm mt-1">
                            {formik.errors.time as string}
                        </Text>
                    )}
                </div>

                {/* CENSUS MIN */}

                <div>
                    <RequiredInput
                        id={'censusMin'}
                        label="Censo (Min)"
                        type="number"
                        min={0}
                        formik={formik}
                        value={formik.values.censusMin ?? ''}
                        onChange={async (e) => {
                            await formik.setFieldValue('censusMin', e.target.value, true);
                            formik.setFieldTouched('censusMin', true, false);
                        }}
                        className="mb-4"
                        error={
                            formik.errors.censusMin &&
                            (formik.touched.censusMin ||
                                (formik.values.censusMin !== null && formik.values.censusMin !== ''))
                                ? formik.errors.censusMin
                                : undefined
                        }
                    />
                </div>

                <div className="md:col-span-2">
                    <WeatherSelectors formik={formik} required visibility syncCalmWind windFieldsFirst />
                </div>

                {/* SPECIE SELECTOR */}

                <div>
                    <CustomSelect
                        isSearchable
                        id={`specieId_${lineId || lineNumber}`}
                        label="Especie"
                        italic
                        options={speciesList}
                        onChange={(e: any) => {
                            formik.setFieldValue('specieId', e.value)
                            if (e.catAndalucia || e.catNacional) {
                                formik.setFieldValue('isProtected', true);
                            }
                            else {
                                formik.setFieldValue('isProtected', false);
                            }
                        }}
                        value={
                            speciesList.filter((specie: any) => specie.value === formik.values.specieId)
                        }
                        required
                        error={formik.touched.specieId && formik.errors.specieId ? formik.errors.specieId : null}
                    />
                </div>

                {/* NUM INDIVIDUALS */}

                <div>
                    <RequiredInput
                        id={`numIndividuals_${lineId || lineNumber}`}
                        placeholder="Número de individuos"
                        value={formik.values.numIndividuals}
                        onChange={(e) => formik.setFieldValue('numIndividuals', e.target.value)}
                        onBlur={formik.handleBlur}
                        name="numIndividuals"
                        label={`Número de Individuos`}
                        required
                        error={formik.touched.numIndividuals && formik.errors.numIndividuals ? formik.errors.numIndividuals : null}
                    />
                </div>

                {/* SEX SELECTOR */}

                <div>
                    <CustomSelect
                        isSearchable
                        id={`sex_${lineId || lineNumber}`}
                        label="Sexo"
                        options={sex}
                        onChange={(e: any) => {
                            formik.setFieldValue('sex', e.value)
                        }}
                        value={
                            formik.values.sex
                                ? sex.filter((status: any) => status.value == formik.values.sex)
                                : null
                        }
                        error={formik.touched.sex && formik.errors.sex ? formik.errors.sex : null}   
                    />
                </div>

                {/* AGE SELECTOR */}

                <div>
                    <CustomSelect
                        isSearchable
                        id={`age_${lineId || lineNumber}`}
                        label="Edad"
                        options={age}
                        onChange={(e: any) => {
                            formik.setFieldValue('age', e.value)
                        }}
                        value={
                            formik.values.age
                                ? age.filter((status: any) => status.value == formik.values.age)
                                : null
                        }
                        error={formik.touched.age && formik.errors.age ? formik.errors.age : null}
                    />
                </div>
                
                <div  className={`md:col-span-2 grid md:grid-cols-3 grid-cols-1 gap-4`}>

                     {/* TURBINE SELECTOR */}
                    <div>
                        <CustomSelect
                            isSearchable
                            id={`turbineId_${lineId || lineNumber}`}
                            label="Aerogenerador"
                            required={true}
                            key={parkId || ""}
                            options={turbinesList}
                            onChange={(e: any) => {
                                formik.setFieldValue('turbineId', e.value)
                            }}
                            value={
                                turbinesList.filter((turbine: any) => turbine.value === formik.values.turbineId)
                            }
                            error={formik.touched.turbineId && formik.errors.turbineId ? formik.errors.turbineId : null}
                        />    
                    </div>

                    {/* ALTITUDE LEVEL SELECTOR */}

                    <div>
                        <CustomSelect
                            id={`altitudLevel_${lineId || lineNumber}`}
                            label="Altitud"
                            required={true}
                            placeholder="Seleccione nivel"
                            options={altitudeLevel}
                            value={formik.values.altitudLevel !== null ? { 
                                value: formik.values.altitudLevel, 
                                label: altitudeLevel.find((level: any) => level.value == formik.values.altitudLevel)?.label || ''
                            } : null}
                            onChange={(option: any) => formik.setFieldValue('altitudLevel', option.value)}
                            error={formik.touched.altitudLevel && formik.errors.altitudLevel ? formik.errors.altitudLevel : null}
                            isDisabled={formik.values.riskDetected}
                        />
                    </div>

                    {/* DIST AERO */}

                    <div>
                        <CustomSelect
                            id={`distanceToAero_${lineId || lineNumber}`}
                            label="Distancia al aero"
                            required={true}
                            customPlaceholder="Seleccione distancia"
                            options={distanceToAero}
                            value={formik.values.distanceToAero ? { 
                                value: formik.values.distanceToAero, 
                                label: formik.values.distanceToAero 
                            } : null}
                            onChange={(option: any) => formik.setFieldValue('distanceToAero', option.value)}
                            error={formik.touched.distanceToAero && formik.errors.distanceToAero ? formik.errors.distanceToAero : null}
                            isDisabled={formik.values.riskDetected}
                        />
                    </div>

                </div>

                <div className={`md:col-span-2 grid md:grid-cols-3 grid-cols-1 gap-4`}>

                    {/* DIRECTION ORIGIN SELECTOR */}

                    <div>
                        <CustomSelect
                            id={`dirOrigen_${lineId || lineNumber}`}
                            label="D. origen vuelo"
                            info="Dirección de origen del vuelo del ave"
                            required={true}
                            customPlaceholder="Seleccione dirección"
                            options={windDirectionsSpanish}
                            value={formik.values.dirOrigen ? { 
                                value: formik.values.dirOrigen, 
                                label: windDirectionsSpanish.find((dir: any) => dir.value === formik.values.dirOrigen)?.label || formik.values.dirOrigen
                            } : null}
                            onChange={(option: any) => formik.setFieldValue('dirOrigen', option.value)}
                            error={formik.touched.dirOrigen && formik.errors.dirOrigen ? formik.errors.dirOrigen : null}
                        />
                    </div>

                    {/* DIRECTION DESTINY SELECTOR */}
                
                    <div>
                        <CustomSelect
                            id={`dirDest_${lineId || lineNumber}`}
                            label="D. destino vuelo"
                            info="Dirección de destino del vuelo del ave"
                            required={true}
                            customPlaceholder="Seleccione dirección"
                            options={windDirectionsSpanish}
                            value={formik.values.dirDest ? { 
                                value: formik.values.dirDest, 
                                label: windDirectionsSpanish.find((dir: any) => dir.value === formik.values.dirDest)?.label || formik.values.dirDest
                            } : null}
                            onChange={(option: any) => formik.setFieldValue('dirDest', option.value)}
                            error={formik.touched.dirDest && formik.errors.dirDest ? formik.errors.dirDest : null}
                        />
                    </div>

                    {/* FLIGHT TYPE SELECTOR */}

                    <div>
                        <CustomSelect
                            isSearchable
                            id={`flightType_${lineId || lineNumber}`}
                            label="Tipo de vuelo"
                            info="Tipo de vuelo del ave (columna VUELO en listados)"
                            options={flightType}
                            onChange={(e: any) => {
                                formik.setFieldValue('flightType', e.value)
                            }}
                            value={
                                formik.values.flightType
                                    ? flightType.filter((type: any) => type.value === formik.values.flightType)
                                    : null
                            }   
                            error={formik.touched.flightType && formik.errors.flightType ? formik.errors.flightType : null}
                        />
                    </div>

                </div>

                {/* REACC */}

                <div>
                    <CustomSelect
                        id={`reacc_${lineNumber}`}
                        label="REACC"
                        required={true}
                        customPlaceholder="Seleccione reacción"
                        options={yesNoSelector}
                        value={formik.values.reacc ? { 
                            value: formik.values.reacc, 
                            label: yesNoSelector.find(option => option.value == formik.values.reacc)?.label || 'N/A'
                        } : null}
                        onChange={(option: any) => formik.setFieldValue('reacc', option.value)}
                        error={formik.touched.reacc && formik.errors.reacc ? formik.errors.reacc : null}
                    />
                </div>

                {/* AERO STATUS */}

                <div className={`grid ${formik.values.aeroStatus === 'apagado' ? 'md:grid-cols-2' : '' } grid-cols-1 gap-4`}>

                    <div>
                        <CustomSelect
                            id={`aeroStatus_${lineId || lineNumber}`}
                            label="Estado del aerogenerador"
                            required={true}
                            placeholder="Seleccione estado"
                            options={aeroStatus}
                            value={formik.values.aeroStatus ? { 
                                value: formik.values.aeroStatus, 
                                label: formik.values.aeroStatus.charAt(0).toUpperCase() + formik.values.aeroStatus.slice(1) 
                            } : null}
                            onChange={(option: any) => {
                                formik.setFieldValue('aeroStatus', option.value);
                                if (option.value === 'apagado') {
                                    formik.setFieldValue('askStop', 'no');
                                }
                            }}
                            error={formik.touched.aeroStatus && formik.errors.aeroStatus ? formik.errors.aeroStatus : null}
                        />
                    </div>
                    
                    {formik.values.aeroStatus === 'apagado' && (
                        <div>
                             <CustomSelect
                                isSearchable
                                id={`aeroOnComments_${lineId || lineNumber}`}
                                label="Motivo de parada"
                                options={statusAeroOptions}
                                onChange={(e: any) => {
                                    formik.setFieldValue('aeroOnComments', e.value);
                                }}
                                value={
                                    formik.values.aeroOnComments
                                    ? statusAeroOptions.find(option => option.value === formik.values.aeroOnComments)
                                    : null
                                }
                                required
                                error={formik.errors.aeroOnComments && formik.touched.aeroOnComments ? formik.errors.aeroOnComments : undefined}
                                />
                        </div>
                    )}

                </div>

                {/* ASK STOP */}
                
                <div>
                    <CustomSelect
                        id={`askStop_${lineNumber}`}
                        label="Se solicita Parada"
                        required={true}
                        placeholder="Seleccione"
                        options={yesNoSelector}
                        value={formik.values.askStop ? { 
                            value: formik.values.askStop, 
                            label: yesNoSelector.find(option => option.value === formik.values.askStop)?.label || 'N/A'
                        } : null}
                        onChange={(option: any) => formik.setFieldValue('askStop', option.value)}
                        isDisabled={formik.values.aeroStatus === 'apagado'}
                        error={formik.touched.askStop && formik.errors.askStop ? formik.errors.askStop : null}
                    />
                </div>

                {/* ASK ANILLADO */}
                
                <div>
                    <CustomSelect
                        isSearchable
                        id={`askAnillado_${lineNumber}`}
                        label="Anillado"
                        options={yesNoSelector}
                        onChange={(e: any) => {
                            formik.setFieldValue('askAnillado', e.value);
                            if (e.value === 'no') {
                                formik.setFieldValue('extraAnillado', '');
                            }
                        }}
                        value={
                            formik.values.askAnillado
                            ? yesNoSelector.find(option => option.value == formik.values.askAnillado)
                            : yesNoSelector.find(option => option.value == 'no')
                        }
                        required={true}
                        error={formik.touched.askAnillado && formik.errors.askAnillado ? formik.errors.askAnillado : null}
                    />
                </div>

                {/* EXTRA INFO ANILLADO */}

                {formik.values.askAnillado === 'yes' && (
                    <div className="md:col-span-2 grid md:grid-cols-1 grid-cols-1 gap-2 mb-4">
                        <Textarea
                            id={`extraAnillado_${lineNumber}`}
                            label='Anillado'
                            rows={6}
                            value={formik.values.extraAnillado || ''}
                            onChange={(e) => formik.setFieldValue('extraAnillado', e.target.value)}
                            className="mb-4"
                        />
                    </div>
                )}

                <div className="md:col-span-2 grid md:grid-cols-2 grid-cols-1 gap-2 mb-4">
                    
                    {/* RISK DETECTED */}

                    <div className="flex items-center">
                        <Switch
                            id={`riskDetected_${lineNumber}`}
                            label="Ave a menos de 5m del aerogenerador"
                            checked={formik.values.riskDetected}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                formik.setFieldValue('riskDetected', isChecked);
                                
                                if (isChecked) {
                                    formik.setFieldValue('distanceToAero', 'A');
                                    formik.setFieldValue('altitudLevel', '2');
                                }
                            }}
                        />
                    </div>
                </div>


                <div className="md:col-span-2 grid md:grid-cols-1 grid-cols-1 gap-2 mb-4">

                    <Textarea
                        id={`anillado_${lineNumber}`}
                        label='Observaciones'
                        rows={6}
                        value={formik.values.anillado || ''}
                        onChange={(e) => formik.setFieldValue('anillado', e.target.value)}
                        className="mb-4"
                    />
                </div>

                {/* PHOTOS SECTION - Only show for protected species */}
                {(formik.values.isProtected || formik.values.askAnillado === 'yes') && (
                    <div className="md:col-span-2 mb-6">
                        <div className="border-t pt-6">
                            <Text className="text-lg font-medium mb-4">Fotos de la Línea</Text>
                            <CensusLinePhotosGallery
                                censusLineId={initialValues?.id || undefined}
                                photos={formik.values.photos}
                                onPhotosChange={(photos: File[]) => formik.setFieldValue('photos', photos)}
                                lineTitle={`Línea ${lineNumber}`}
                            />
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default CensusLineForm;